import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { useCurrentSite } from '../lib/currentSite';

type TabKey = 'home' | 'sites' | 'records' | 'registers' | 'settings';

function HomeIcon() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1V9.5z" /></svg>;
}

function SitesIcon() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s7-6.5 7-12a7 7 0 1 0-14 0c0 5.5 7 12 7 12z" /><circle cx="12" cy="10" r="2.6" /></svg>;
}

function RecordsIcon() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 16 14" /></svg>;
}

function RegistersIcon() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v6H4z" /><path d="M4 14h16v6H4z" /><circle cx="8" cy="7" r="0.5" fill="currentColor" /><circle cx="8" cy="17" r="0.5" fill="currentColor" /></svg>;
}

function SettingsIcon() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6h.09c.65-.27 1.07-.92 1-1.62V3a2 2 0 0 1 4 0v.09c-.07.7.35 1.35 1 1.62h.09a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.27.65.92 1.07 1.62 1H21a2 2 0 0 1 0 4h-.09c-.7-.07-1.35.35-1.51 1z" /></svg>;
}

function detectActive(pathname: string): TabKey | null {
  // Home tab is "lit" on `/` (the active-site Dashboard) and the legacy
  // `/dashboard` alias. Sites tab is lit on `/sites` and any per-site
  // route — once you're inside a site, you're conceptually in "Sites".
  if (pathname === '/' || pathname === '/dashboard') return 'home';
  if (pathname.includes('/records')) return 'records';
  if (pathname.startsWith('/registers')) return 'registers';
  if (pathname.startsWith('/settings')) return 'settings';
  if (pathname.startsWith('/sites') || pathname === '/completed-jobs') return 'sites';
  return null;
}

export function BottomTabBar() {
  const { currentSiteId } = useCurrentSite();
  const { pathname } = useLocation();
  const active = detectActive(pathname);

  // Auto-hide behaviour:
  //   - Hide when scrolling down past 60px on the main content
  //   - Show when scrolling up
  //   - Always show at the top of a page
  //   - Hide when an input/textarea is focused (keyboard up)
  const [hidden, setHidden] = useState(false);
  const lastScrollRef = useRef(0);

  useEffect(() => {
    // Reset visibility on route change so the bar is always shown when
    // landing on a new page.
    setHidden(false);
    lastScrollRef.current = 0;
  }, [pathname]);

  useEffect(() => {
    const main = document.querySelector('.app-main') as HTMLElement | null;
    if (!main) return;

    // Auto-hide is a MOBILE pattern — it reclaims a chunk of screen the user
    // can't afford to lose on a phone. On desktop the screen is huge and the
    // bar isn't in the way; collapsing/expanding its layout space on scroll
    // just makes the page jiggle (we saw flicker on the home page where
    // scroll-up → bar shows → padding-bottom expands → tiny layout shift →
    // bar hides → flicker). Below 720px we keep the mobile behaviour; at and
    // above we just always show it.
    const desktopMq = window.matchMedia('(min-width: 720px)');

    let ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        if (!main) { ticking = false; return; }
        // Desktop → never auto-hide.
        if (desktopMq.matches) {
          setHidden(false);
          ticking = false;
          return;
        }
        // Only enable the auto-hide behaviour if the page has enough
        // scrollable content to justify it — otherwise short pages (like
        // the home dashboard) flicker as the layout-collapse keeps
        // bouncing scrollTop around.
        const scrollable = main.scrollHeight - main.clientHeight;
        if (scrollable < 120) {
          setHidden(false);
          ticking = false;
          return;
        }

        const y = main.scrollTop;
        const last = lastScrollRef.current;
        const atBottom = (main.scrollHeight - y - main.clientHeight) < 80;

        // SHOW the bar whenever the user is somewhere they'd want to
        // navigate away from:
        //   - At the very top of the page
        //   - Within 80px of the bottom (user finished reading / finished
        //     a long form; this is the case Will hit on tick lists)
        //   - Scrolling upward by any meaningful amount (>4px)
        if (y < 60 || atBottom) {
          setHidden(false);
        } else if (y > last + 6) {
          // Scrolling DOWN past threshold → hide.
          setHidden(true);
        } else if (y < last - 4) {
          // Any small upward scroll → show. (Was 18px + 400ms lock; both
          // were causing the bar to stay hidden after a gentle drag back up.)
          setHidden(false);
        }
        lastScrollRef.current = y;
        ticking = false;
      });
    }

    main.addEventListener('scroll', onScroll, { passive: true });
    return () => main.removeEventListener('scroll', onScroll);
  }, [pathname]);

  // Hide while typing — when a text input or textarea has focus, the iOS
  // keyboard takes most of the screen. The tab bar isn't useful at that point.
  //
  // Safety net: we also force the bar to re-show on any tap or click that
  // doesn't land on a text input. iOS Safari sometimes fails to fire a
  // clean `focusout` when the keyboard is dismissed (Done button, swipe-
  // down on the keyboard, etc.) which used to leave the bar stuck hidden.
  useEffect(() => {
    function isTextInput(el: Element | null): boolean {
      if (!el) return false;
      const tag = el.tagName;
      if (tag === 'TEXTAREA' || (el as HTMLElement).isContentEditable) return true;
      if (tag !== 'INPUT') return false;
      const type = (el as HTMLInputElement).type?.toLowerCase();
      const nonText = ['checkbox', 'radio', 'button', 'submit', 'file', 'date', 'color', 'range'];
      return !nonText.includes(type ?? '');
    }

    function onFocus(e: FocusEvent) {
      if (isTextInput(e.target as Element)) {
        setHidden(true);
      }
    }
    function onBlur() {
      // Slight delay so focus moving between inputs doesn't show/hide.
      setTimeout(() => {
        if (!isTextInput(document.activeElement)) {
          setHidden(false);
        }
      }, 100);
    }
    function onPointerDown(e: PointerEvent) {
      // Safety net: any tap that DOESN'T land on a text input forces the
      // bar to show. This rescues the user from the "iOS-didn't-fire-blur"
      // failure mode where the bar stays hidden after typing.
      if (!isTextInput(e.target as Element)) {
        setHidden(false);
      }
    }

    document.addEventListener('focusin', onFocus);
    document.addEventListener('focusout', onBlur);
    document.addEventListener('pointerdown', onPointerDown, { passive: true });
    return () => {
      document.removeEventListener('focusin', onFocus);
      document.removeEventListener('focusout', onBlur);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, []);

  const fallback = '/sites';
  // Fixed destinations — no more "smart Home" routing. Home is always the
  // active-site Dashboard (or its empty-state when no site is set). Sites
  // is the picker + manager — that's where you go to switch sites.
  const tabs: { key: TabKey; label: string; to: string; icon: React.ReactNode }[] = [
    { key: 'home',      label: 'Home',      to: '/',                                                            icon: <HomeIcon /> },
    { key: 'sites',     label: 'Sites',     to: '/sites',                                                       icon: <SitesIcon /> },
    // Tab key stays 'records' so the active-state detection + route still
    // match `/sites/:id/records` — only the visible label changes. "Activity"
    // pairs naturally with "Registers" alongside it: what happened on this
    // site vs what we have on file across the org. "Records" was too
    // generic and collided with "Registers" in builder vocabulary.
    { key: 'records',   label: 'Activity',  to: currentSiteId ? `/sites/${currentSiteId}/records` : fallback,   icon: <RecordsIcon /> },
    { key: 'registers', label: 'Registers', to: '/registers',                                                   icon: <RegistersIcon /> },
    { key: 'settings',  label: 'Settings',  to: '/settings',                                                    icon: <SettingsIcon /> },
  ];

  // Mirror the hidden state on <body> so the .app-container can collapse
  // its bottom padding (green strip) in sync with the tab bar sliding away.
  useEffect(() => {
    document.body.classList.toggle('tabs-hidden', hidden);
    return () => { document.body.classList.remove('tabs-hidden'); };
  }, [hidden]);

  return (
    <nav className={`bottom-tab-bar${hidden ? ' bottom-tab-bar-hidden' : ''}`} aria-label="Primary navigation">
      {tabs.map((t) => (
        <Link
          key={t.key}
          to={t.to}
          className={`bottom-tab ${active === t.key ? 'active' : ''}`}
          aria-current={active === t.key ? 'page' : undefined}
        >
          <span className="bottom-tab-icon">{t.icon}</span>
          <span className="bottom-tab-label">{t.label}</span>
        </Link>
      ))}
    </nav>
  );
}
